/**
 * Reading a link credential the server never saw, and replaying it in a header.
 *
 * A magic link, a CSAT link and a freight-quote approval link are all
 * bearer-equivalent: whoever holds the string is the caller. They used to arrive
 * as `?token=` (and, for the freight quote, as a path segment), which puts the
 * credential in the request LINE — recorded by every intermediary, kept in
 * browser history, leaked through `Referer`, and since WI 3c4834eb written to
 * the ALB and CloudFront access logs for 90 days.
 *
 * So the email now points at `…/page#token=…`. A fragment is the one part of a
 * URL the browser keeps to itself — it is never put on the wire — and this
 * module is what turns it back into a credential the API can read, in the
 * `X-Public-Token` header. Header, because neither access-log field set records
 * one. Backend counterpart: `shared/dependencies/public_token.py`.
 *
 * Modelled on `modules/people/kiosk/kioskApi.ts`, the one other surface here
 * that authenticates with something other than the user JWT: like it, callers
 * must bypass the shared axios instance, which would inject the logged-in user's
 * token instead.
 */

/** The one header an unauthenticated link credential travels on. */
export const PUBLIC_TOKEN_HEADER = 'X-Public-Token';

/** The fragment key the backend's link builders write. */
const FRAGMENT_TOKEN_KEY = 'token';

/**
 * Lift the link credential out of `location.hash`. PURE — reads nothing else and
 * changes nothing.
 *
 * Purity is load-bearing, not tidiness: this is called as a `useState` lazy
 * initializer, and React invokes those TWICE on mount under `<StrictMode>`. An
 * earlier cut erased the fragment inside this function, so the second invocation
 * saw an already-empty hash and returned `''` — making all three landing pages
 * render "this link is invalid or has expired" on every load in development,
 * while production (one invocation) worked. Erasing is a separate call:
 * {@link eraseFragmentToken}.
 *
 * Returns `''` when the fragment carries no token — a first-class state, not an
 * error: it is what an already-sent email carrying the old `?token=` shape now
 * does, and every landing page routes it to that same panel rather than
 * half-working. There is deliberately no `?token=` fallback; reading one here
 * would keep the leak alive for exactly the callers this protects.
 */
export function readFragmentToken(): string {
  const hash = window.location.hash ?? '';
  const match = new RegExp(`(?:^#|&)${FRAGMENT_TOKEN_KEY}=([^&]*)`).exec(hash);
  if (!match) return '';

  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed percent-escape is an unusable credential, not a crash.
    return '';
  }
}

/**
 * Drop the credential from the address bar, so it does not survive into a
 * bookmark, a screen share or a copied URL.
 *
 * Idempotent — safe to call again on a hash that no longer carries a token,
 * which is what makes it correct in a `useEffect` that StrictMode runs twice.
 * Call it AFTER {@link readFragmentToken}, whose return value is the only copy
 * the page needs.
 */
export function eraseFragmentToken(): void {
  if (!window.location.hash) return;
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    // Older browsers / restricted contexts: keeping the fragment is a cosmetic
    // loss, never a functional one — it was never transmitted either way.
  }
}

/** Request headers carrying a link credential. */
export function publicTokenHeaders(token: string): Record<string, string> {
  return { [PUBLIC_TOKEN_HEADER]: token };
}
